
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';

const stats = [
  { value: '1,247+', label: '진행중인 공고', color: 'text-neon-pink' },
  { value: '350+', label: '파트너 업체', color: 'text-neon-purple' },
  { value: '5,820+', label: '성공 매칭', color: 'text-emerald-400' },
];

const quickTags = ['DJ', '바텐더', '웨이터', '매니저', '호스트', '보안요원'];

export default function HeroSection() {
  const [scrolled, setScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/jobs?q=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate('/jobs');
    }
  };

  return (
    <section className="relative overflow-hidden bg-gradient-purple" style={{ minHeight: '72vh' }}>
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-neon-purple rounded-full blur-[130px] opacity-15"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-neon-pink rounded-full blur-[110px] opacity-10"></div>
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        ></div>
      </div>

      {/* Navigation */}
      <Navigation scrolled={scrolled} />

      {/* Hero Content — centered */}
      <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 pt-36 pb-16">

        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6 backdrop-blur-sm"
        >
          <span className="w-2 h-2 rounded-full bg-neon-pink animate-pulse"></span>
          <span className="text-white/80 text-sm font-medium">나이트라이프 업계 전문 구인구직</span>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-5xl font-black text-white leading-tight mb-4 max-w-2xl"
          style={{ textShadow: '0 0 40px rgba(168,85,247,0.25)' }}
        >
          밤을 빛내는<br />
          <span className="bg-gradient-to-r from-neon-pink to-neon-purple bg-clip-text text-transparent">
            당신의 무대
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-slate-300 text-base font-light mb-8 max-w-md"
        >
          개인회원 무료 · 업계 최다 공고 · 빠른 매칭
        </motion.p>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="w-full max-w-xl mb-4"
        >
          <div className="flex items-center bg-white/10 border border-white/20 backdrop-blur-md rounded-full h-14 px-2 gap-2">
            <div className="w-10 h-10 flex items-center justify-center text-white/50 flex-shrink-0">
              <i className="ri-search-line text-lg"></i>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="직종, 지역, 키워드 검색"
              className="flex-1 bg-transparent text-white placeholder-white/40 text-sm outline-none"
            />
            <button
              onClick={handleSearch}
              className="bg-gradient-pink text-white text-sm font-semibold px-6 h-10 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity flex-shrink-0"
            >
              검색
            </button>
          </div>
        </motion.div>

        {/* Quick Tags */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="flex flex-wrap justify-center gap-2 mb-12"
        >
          {quickTags.map((tag) => (
            <button
              key={tag}
              onClick={() => navigate(`/jobs?q=${encodeURIComponent(tag)}`)}
              className="px-4 py-1.5 rounded-full border border-white/20 text-white/70 text-xs hover:bg-neon-pink/20 hover:border-neon-pink hover:text-white transition-all cursor-pointer whitespace-nowrap"
            >
              {tag}
            </button>
          ))}
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="flex items-center gap-6"
        >
          {stats.map((stat, i) => (
            <div key={i} className="flex flex-col items-center bg-white/8 border border-white/10 backdrop-blur-sm rounded-2xl px-8 py-4 min-w-[140px]">
              <span className={`text-2xl font-black ${stat.color}`}>{stat.value}</span>
              <span className="text-white/50 text-xs mt-1">{stat.label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
